import {getInvoiceBusinessId} from '@afriex/core/utils';

import {Badge} from '@/components';
import type {AdminBusinessInvoice} from '@/types';
import {invoiceStatusColors} from '@/types/AdminBusinessInvoice';
import {capitalizeWords, formatValues} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

const Field = ({
  label,
  value,
  dataCy,
}: {
  label: string;
  value: React.ReactNode;
  dataCy?: string;
}) => (
  // min-w-0 lets a long unspaced value break instead of widening the grid column.
  <div className="flex min-w-0 flex-col" data-cy={dataCy}>
    <span className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
      {label}
    </span>
    <span className="mt-1 text-sm text-gray-800 dark:text-gray-100">
      {value}
    </span>
  </div>
);

const formatEnumLabel = (value: string) =>
  capitalizeWords(value.replace(/_/g, ' '));

/**
 * Expanded detail for one invoice row. Mirrors FeeSummary: a read-only field
 * grid, no actions. Recording payments and cancelling live on the server side
 * and are not part of this surface yet.
 */
const InvoiceSummary = ({
  item,
  dataCy,
}: {
  item: AdminBusinessInvoice;
  /** Test hook prefix; each rendered field namespaces under it. */
  dataCy?: string;
}) => {
  const amountPaid = item.amount.paid ?? 0;
  // amountDue may be absent; derive rather than render a blank, since "what is
  // still owed" is the number personnel came here for.
  const amountDue = item.amount.due ?? item.amount.value - amountPaid;
  const fieldCy = (name: string) => (dataCy ? `${dataCy}-${name}` : undefined);

  return (
    <div
      className="whitespace-normal break-words w-full max-w-4xl"
      data-cy={dataCy}
    >
      <div className="flex items-center justify-between">
        <span className="text-lg font-semibold text-gray-800 dark:text-gray-100">
          {item.meta.reference}
        </span>
        <Badge
          label={formatEnumLabel(item.meta.status)}
          colorScheme={invoiceStatusColors[item.meta.status]}
        />
      </div>

      <div className="mt-6 grid grid-cols-2 gap-5 md:grid-cols-3">
        <Field
          label="Merchant"
          value={item.meta.businessName || getInvoiceBusinessId(item) || ''}
          dataCy={fieldCy('merchant')}
        />
        <Field
          label="Amount"
          value={`${formatValues(item.amount.value, 'money')} ${item.amount.currencyCode}`}
          dataCy={fieldCy('amount')}
        />
        <Field
          label="Paid"
          value={`${formatValues(amountPaid, 'money')} ${item.amount.currencyCode}`}
          dataCy={fieldCy('paid')}
        />
        <Field
          label="Due"
          value={`${formatValues(amountDue, 'money')} ${item.amount.currencyCode}`}
          dataCy={fieldCy('due')}
        />
        <Field
          label="Period start"
          value={formatDate.DDMonYYYY(String(item.dates.periodStart))}
          dataCy={fieldCy('period-start')}
        />
        <Field
          label="Period end"
          value={formatDate.DDMonYYYY(String(item.dates.periodEnd))}
          dataCy={fieldCy('period-end')}
        />
        <Field
          label="Payment due"
          value={formatDate.DDMonYYYY(String(item.dates.dueDate))}
          dataCy={fieldCy('payment-due')}
        />
        <Field
          label="Created"
          value={formatDate.DDMonYYYY(String(item.createdAt))}
          dataCy={fieldCy('created')}
        />
        <Field
          label="Last updated"
          value={formatDate.DDMonYYYY(String(item.updatedAt))}
          dataCy={fieldCy('updated')}
        />
        <Field
          label="Description"
          value={item.meta.description || '—'}
          dataCy={fieldCy('description')}
        />
      </div>

      {/* The details as they were when the invoice was raised, not as the
          account reads today. The snapshot is flat: it is a copy of the
          settlement method's bank fields, not the method itself. */}
      {item.meta.settlementAccount ? (
        <div className="mt-8">
          <span className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Paid into
          </span>
          <div className="mt-2 grid grid-cols-2 gap-5 md:grid-cols-3">
            <Field
              label="Account name"
              value={item.meta.settlementAccount.accountName}
              dataCy={fieldCy('settlement-account-name')}
            />
            <Field
              label="Account number"
              value={item.meta.settlementAccount.accountNumber}
              dataCy={fieldCy('settlement-account-number')}
            />
            <Field
              label="Bank"
              value={item.meta.settlementAccount.institutionName}
              dataCy={fieldCy('settlement-bank')}
            />
            <Field
              label="SWIFT"
              value={item.meta.settlementAccount.swiftCode || '—'}
            />
            <Field
              label="Routing number"
              value={item.meta.settlementAccount.routingNumber || '—'}
            />
            <Field
              label="Instructions"
              value={item.meta.settlementAccount.instructions || '—'}
            />
          </div>
        </div>
      ) : null}

      {item.meta.documentUrl ? (
        <a
          href={item.meta.documentUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-6 inline-block text-sm font-medium text-cyan-600 hover:underline dark:text-cyan-400"
        >
          Download invoice PDF
        </a>
      ) : (
        <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
          No PDF generated for this invoice yet.
        </p>
      )}
    </div>
  );
};

export default InvoiceSummary;
